/**
 * PARÇA 437 — Public terms of service page.
 *
 * Hosted at https://event.dayivo.com/terms. Full long-form ToS lives in
 * `event/legal/terms_of_service_en.md`. Pending Canadian commercial-law
 * lawyer review; draft banner stays until cleared.
 */
import React from 'react';

export function TermsPage(): React.JSX.Element {
  return (
    <main className="mx-auto max-w-3xl px-6 py-10 text-gray-900 dark:text-gray-100">
      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Terms of Service</h1>
        <p className="mt-2 text-sm text-gray-500">Last updated: 2026-05-31 (Draft pending lawyer review)</p>
      </header>

      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
        <strong>Draft notice:</strong> This page is a developer draft. The final version will be reviewed by a Canadian commercial lawyer before public launch.
      </div>

      <section className="prose prose-sm mt-8 dark:prose-invert">
        <h2>1. Acceptance</h2>
        <p>By creating an account or using Nightlife (the &quot;Service&quot;), you agree to these Terms. If you don&apos;t agree, don&apos;t use the Service.</p>

        <h2>2. Who can use Nightlife</h2>
        <ul>
          <li>You must be at least <strong>17 years old</strong>.</li>
          <li>You can only have one account.</li>
          <li>You agree to provide accurate information (real birth date for age-gating).</li>
          <li>You won&apos;t impersonate anyone.</li>
        </ul>

        <h2>3. Your content</h2>
        <p>You keep ownership of everything you post. You grant us a worldwide, royalty-free license to display it within the Service for the duration of your account.</p>
        <p>You promise your content doesn&apos;t infringe anyone&apos;s rights, isn&apos;t unlawful/harassing/hateful/sexually explicit/threatening, doesn&apos;t impersonate, doesn&apos;t promote underage drinking, drug use, or violence, and doesn&apos;t include personal information about others without consent. We can remove content that violates these rules. Repeat violations lead to account termination.</p>

        <h2>4. What you can&apos;t do</h2>
        <ul>
          <li>Reverse-engineer, decompile, or scrape the Service.</li>
          <li>Use bots, scripts, or automated systems.</li>
          <li>Sell, transfer, or rent your account.</li>
          <li>Use the Service to send spam.</li>
          <li>Try to access other users&apos; accounts.</li>
          <li>Disrupt the Service (DDoS, exploits).</li>
        </ul>

        <h2>5. Venue / DJ / event listings</h2>
        <p>Listings are user-submitted or pulled from public sources. We don&apos;t verify accuracy. We&apos;re not responsible for what happens at a venue or event. Always confirm with the venue before going.</p>

        <h2>6. Boost / paid features</h2>
        <p>Boost (paid promotion of events or venues by owners) is currently disabled. When launched, payments processed by Stripe (subject to Stripe&apos;s terms); duration + pricing displayed before purchase; refunds pro-rated if cancelled before end date.</p>

        <h2>7. Realtime communication</h2>
        <p>Direct messages and group chats are end-to-end encrypted <strong>in transit only</strong>. We have access to message content for moderation, abuse prevention, and legal compliance. We do not read messages otherwise.</p>

        <h2>8. Termination</h2>
        <p>You can delete your account at any time (Settings → Privacy → Delete Account). We can suspend or terminate for Terms violations, suspected fraud, legal requirements, or 12+ months of inactivity. On termination, your content becomes inaccessible to others within 24 hours.</p>

        <h2>9. Disclaimers</h2>
        <p>THE SERVICE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTY OF ANY KIND. We don&apos;t guarantee uninterrupted service, accurate listings, that venues will honor displayed information, or that other users will behave appropriately. YOU USE THE SERVICE AT YOUR OWN RISK.</p>

        <h2>10. Limitation of liability</h2>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ANY CLAIM IS LIMITED TO THE GREATER OF $100 CAD OR THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM. We are not liable for indirect, consequential, special, or punitive damages. These limits do not apply to liability that cannot be excluded under Canadian consumer protection law.</p>

        <h2>11. Indemnification</h2>
        <p>You agree to indemnify us against any claim arising from your content, your use of the Service, or your violation of these Terms.</p>

        <h2>12. Modifications</h2>
        <p>We can change these Terms. Material changes announced in the app at least 30 days before they take effect.</p>

        <h2>13. Governing law</h2>
        <p>These Terms are governed by the laws of the applicable Canadian province where Dayivo is registered. Disputes will be resolved in courts of that jurisdiction.</p>
        <p><strong>For Quebec residents:</strong> Nothing here waives your rights under the Consumer Protection Act.</p>

        <h2>14. Contact</h2>
        <p>
          Support: <a href="mailto:support@event.dayivo.com">support@event.dayivo.com</a><br />
          Legal: <a href="mailto:legal@event.dayivo.com">legal@event.dayivo.com</a>
        </p>
      </section>

      <footer className="mt-12 border-t border-gray-200 pt-6 text-sm text-gray-500 dark:border-gray-700">
        <a href="/" className="text-blue-600 hover:underline dark:text-blue-400">Back to home</a>
        <span className="mx-2">·</span>
        <a href="/privacy" className="text-blue-600 hover:underline dark:text-blue-400">Privacy Policy</a>
        <span className="mx-2">·</span>
        <a href="/support" className="text-blue-600 hover:underline dark:text-blue-400">Support</a>
      </footer>
    </main>
  );
}
